import React, { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { auth, authReady } from "../lib/firebase";
import AdminPanel from "./AdminPanel";
import { CONFIG } from "../config";
import "./AdminGate.css"; // optional small styles

const ADMIN_EMAILS = Array.isArray(CONFIG.ADMIN_EMAILS) ? CONFIG.ADMIN_EMAILS : [];
const normalizeEmail = (value) => String(value || "").trim().toLowerCase();
const toFriendlyAuthError = (error) => {
  const code = String(error?.code || "").trim();
  if (code === "auth/invalid-credential" || code === "auth/wrong-password") {
    return "Invalid email or password.";
  }
  if (code === "auth/user-not-found") {
    return "This admin account does not exist in Firebase Authentication.";
  }
  if (code === "auth/invalid-email") {
    return "Please enter a valid email address.";
  }
  if (code === "auth/too-many-requests") {
    return "Too many failed attempts. Please wait and try again.";
  }
  if (code === "auth/user-disabled") {
    return "This admin account is disabled in Firebase Authentication.";
  }
  return String(error?.message || "Login failed");
};

export default function AdminGate() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const normalizedAdminEmails = useMemo(
    () => ADMIN_EMAILS.map((email) => normalizeEmail(email)).filter(Boolean),
    []
  );
  const userEmail = normalizeEmail(user?.email);
  const isAuthorizedAdmin = userEmail && normalizedAdminEmails.includes(userEmail);

  useEffect(() => {
    let unsub = () => {};
    let active = true;

    (async () => {
      try {
        await authReady;
      } catch (persistenceError) {
        console.warn("Auth persistence setup warning:", persistenceError);
      }

      if (!active) return;
      unsub = onAuthStateChanged(auth, (u) => {
        if (!active) return;
        setUser(u);
        setChecking(false);
      });
    })();

    return () => {
      active = false;
      unsub();
    };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    const submittedEmail = normalizeEmail(form.email);
    if (!submittedEmail) {
      setErr("Please enter a valid email address.");
      return;
    }
    if (!normalizedAdminEmails.includes(submittedEmail)) {
      setErr(
        "This email is not in the admin allowlist. Add it to CONFIG.ADMIN_EMAILS and your Firebase database rules."
      );
      return;
    }
    try {
      await authReady;
      await signInWithEmailAndPassword(auth, submittedEmail, form.password);
    } catch (e1) {
      setErr(toFriendlyAuthError(e1));
    }
  };

  if (checking) return <div style={{ padding: 24 }}>Checking session…</div>;

  // Signed in & allowed
  if (isAuthorizedAdmin) {
    return <AdminPanel />;
  }

  // Signed in but not allowed
  if (user && !isAuthorizedAdmin) {
    return (
      <div className="admingate-wrap">
        <div className="admingate-card">
          <h2>Access denied</h2>
          <p>This account is not authorized to edit the database.</p>
          <p style={{ marginTop: 8, fontSize: 14, color: "#6b7280" }}>
            Signed in as: <strong>{user?.email || "Unknown account"}</strong>
          </p>
          <p style={{ marginTop: 8, fontSize: 13, color: "#6b7280" }}>
            Add this email to <code>CONFIG.ADMIN_EMAILS</code> and Firebase DB rules,
            then sign in again.
          </p>
          <button onClick={() => signOut(auth)} className="admingate-btn">Sign out</button>
        </div>
      </div>
    );
  }

  // Not signed in → show email/password form
  return (
    <div className="admingate-wrap">
      <form className="admingate-card" onSubmit={onSubmit}>
        <h2>Admin login</h2>
        <label>
          Email
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Enter admin email"
            required
          />
        </label>

        <label>
          Password
          <input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Enter password"
            required
          />
        </label>

        {err && <p className="admingate-error">{err}</p>}

        <button type="submit" className="admingate-btn primary">Sign in</button>
      </form>
    </div>
  );
}
